<template>
  <div class="single-voucher-form">
    <!-- Voucher Method -->
    <div class="field-group pt-2">
      <div class="field-group-content">
        <div class="field-wrapper">
          <label class="block mb-2">
            {{ t('automatedScenarios.actions.sending_channel.vouchers_config.method.label') }}
            <i
              v-tooltip="{ value: t('automatedScenarios.actions.sending_channel.vouchers_config.method.tooltip'), class: 'tooltip-icons-outside', escape: true }"
              class="fas fa-question-circle"
              aria-hidden="true"
            />
          </label>
          <div class="p-fluid flex">
            <Dropdown
              v-model="localData.method"
              class="col-12 p-0"
              :class="{ 'p-invalid': formValidation?.method?.$error }"
              :options="voucherMethodOptions"
              option-label="label"
              option-value="value"
            />
          </div>
          <small
            v-if="formValidation?.method?.$error"
            class="p-error block text-left"
          >
            {{ t('automatedScenarios.actions.sending_channel.vouchers_config.method.required') }}
          </small>
        </div>
      </div>
    </div>

    <!-- Manual Code (when method is MANUAL) -->
    <div
      v-if="localData.method === VOUCHER_METHODS.MANUAL"
      class="field-group pt-3"
    >
      <div class="field-group-content">
        <div class="field-wrapper">
          <label class="block mb-2">
            {{ t('automatedScenarios.actions.sending_channel.vouchers_config.manual_code.label') }}
            <i
              v-tooltip="{ value: t('automatedScenarios.actions.sending_channel.vouchers_config.manual_code.tooltip'), class: 'tooltip-icons-outside' }"
              class="fas fa-question-circle"
              aria-hidden="true"
            />
          </label>
          <div class="p-fluid flex">
            <InputText
              v-model="localData.identifiant"
              type="text"
              :class="{ 'p-invalid': formValidation?.identifiant?.$error }"
            />
          </div>
          <small
            v-if="formValidation?.identifiant?.$error"
            class="p-error block text-left"
          >
            {{ t('automatedScenarios.actions.sending_channel.vouchers_config.manual_code.required') }}
          </small>
        </div>
      </div>
    </div>

    <!-- Description (when method is AUTOMATIC) -->
    <div
      v-if="localData.method === VOUCHER_METHODS.AUTOMATIC"
      class="field-group pt-3"
    >
      <div class="field-group-content">
        <div class="field-wrapper">
          <label class="block mb-2">
            {{ t('automatedScenarios.actions.sending_channel.vouchers_config.description.label') }}
          </label>
          <div class="p-fluid flex">
            <LocalizedTextInput
              :id="`voucher_descript_${voucherId}`"
              v-model="localData.descript"
              class="w-full"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Voucher Type -->
    <div class="field-group pt-3">
      <div class="field-group-content">
        <div class="field-wrapper">
          <label class="block mb-2">
            {{ t('automatedScenarios.actions.sending_channel.vouchers_config.type.label') }}
            <i
              v-tooltip="{ value: t('automatedScenarios.actions.sending_channel.vouchers_config.type.tooltip'), class: 'tooltip-icons-outside' }"
              class="fas fa-question-circle"
              aria-hidden="true"
            />
          </label>
          <div class="p-fluid flex">
            <Dropdown
              v-model="localData.type"
              class="col-12 p-0"
              :class="{ 'p-invalid': formValidation?.type?.$error }"
              :options="voucherTypeOptions"
              option-label="label"
              option-value="value"
            />
          </div>
          <small
            v-if="formValidation?.type?.$error"
            class="p-error block text-left"
          >
            {{ t('automatedScenarios.actions.sending_channel.vouchers_config.type.required') }}
          </small>
        </div>
      </div>
    </div>

    <!-- Amount (Percentage) -->
    <div
      v-if="localData.type === VOUCHER_TYPES.PERCENTAGE"
      class="field-group pt-3"
    >
      <div class="field-group-content">
        <div class="field-wrapper">
          <label class="block mb-2">
            {{ t('automatedScenarios.actions.sending_channel.vouchers_config.amount.label_percentage') }}
          </label>
          <div class="p-fluid flex">
            <div class="p-inputgroup">
              <span class="p-inputgroup-addon">%</span>
              <InputNumber
                v-model="localData.amount"
                :min="1"
                :max="100"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Amount (Fixed) -->
    <div
      v-if="localData.type === VOUCHER_TYPES.AMOUNT"
      class="field-group pt-3"
    >
      <div class="field-group-content">
        <div class="field-wrapper">
          <label class="block mb-2">
            {{ t('automatedScenarios.actions.sending_channel.vouchers_config.amount.label_amount') }}
          </label>
          <div class="p-fluid flex">
            <div class="p-inputgroup">
              <span class="p-inputgroup-addon">{{ currency }}</span>
              <InputNumber
                v-model="localData.amount"
                :min="1"
                :max="10000000"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Duplicate Code (when AUTOMATIC + DUPLICATED) -->
    <div
      v-if="localData.method === VOUCHER_METHODS.AUTOMATIC
        && localData.type === VOUCHER_TYPES.DUPLICATED"
      class="field-group pt-3"
    >
      <div class="field-group-content">
        <div class="field-wrapper">
          <label class="block mb-2">
            {{ t('automatedScenarios.actions.sending_channel.vouchers_config.duplicate_code.label') }}
          </label>
          <div class="p-fluid flex">
            <InputText
              v-model="localData.duplicate"
              type="text"
              :class="{ 'p-invalid': formValidation?.duplicate?.$error }"
            />
          </div>
          <small
            v-if="formValidation?.duplicate?.$error"
            class="p-error block text-left"
          >
            {{ t('automatedScenarios.actions.sending_channel.vouchers_config.duplicated_code.required') }}
          </small>
        </div>
      </div>
    </div>

    <!-- Minimum Order Amount (when AUTOMATIC) -->
    <div
      v-if="localData.method === VOUCHER_METHODS.AUTOMATIC"
      class="field-group pt-3"
    >
      <div class="field-group-content">
        <div class="field-wrapper">
          <label class="block mb-2">
            {{ t('automatedScenarios.actions.sending_channel.vouchers_config.minimum_amount.label') }}
            <i
              v-tooltip="{ value: t('automatedScenarios.actions.sending_channel.vouchers_config.minimum_amount.tooltip'), class: 'tooltip-icons-outside' }"
              class="fas fa-question-circle"
              aria-hidden="true"
            />
          </label>
          <div class="p-fluid flex">
            <div class="p-inputgroup">
              <span class="p-inputgroup-addon">{{ currency }}</span>
              <InputNumber
                v-model="localData.minorderamount"
                show-buttons
                :min="0"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Period of Validity (when AUTOMATIC) -->
    <div
      v-if="localData.method === VOUCHER_METHODS.AUTOMATIC"
      class="field-group pt-3"
    >
      <div class="field-group-content">
        <div class="field-wrapper">
          <label class="block mb-2">
            {{ t('automatedScenarios.actions.sending_channel.vouchers_config.period_of_validity.label') }}
            <i
              v-tooltip="{ value: t('automatedScenarios.actions.sending_channel.vouchers_config.period_of_validity.tooltip'), class: 'tooltip-icons-outside' }"
              class="fas fa-question-circle"
              aria-hidden="true"
            />
          </label>
          <div class="p-fluid flex">
            <Dropdown
              v-model="localData.validtime"
              class="col-12 p-0"
              :class="{ 'p-invalid': formValidation?.validtime?.$error }"
              :options="periodOfValidityOptions"
              option-label="label"
              option-value="value"
            />
          </div>
          <small
            v-if="formValidation?.validtime?.$error"
            class="p-error block text-left"
          >
            {{ t('automatedScenarios.actions.sending_channel.vouchers_config.period_of_validity.required') }}
          </small>
        </div>
      </div>
    </div>

    <!-- Prefix (when AUTOMATIC) -->
    <div
      v-if="localData.method === VOUCHER_METHODS.AUTOMATIC"
      class="field-group pt-3"
    >
      <div class="field-group-content">
        <div class="field-wrapper">
          <label class="block mb-2">
            {{ t('automatedScenarios.actions.sending_channel.vouchers_config.prefix.label') }}
            <i
              v-tooltip="{ value: t('automatedScenarios.actions.sending_channel.vouchers_config.prefix.tooltip'), class: 'tooltip-icons-outside' }"
              class="fas fa-question-circle"
              aria-hidden="true"
            />
          </label>
          <div class="p-fluid flex">
            <InputText
              v-model="localData.prefix"
              type="text"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Dropdown from 'primevue/dropdown';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import Tooltip from 'primevue/tooltip';
import LocalizedTextInput from '@/components/fields/LocalizedTextInput.vue';
import { UserState } from '@/composables/User';
import { DEFAULT_CURRENCY } from '@/components/template-builder/utils/constants';
import {
  voucherMethodsValues,
  voucherTypeValues,
} from '@/composables/flows';
import type { VoucherConfigItem } from './MultiVoucherConfig.vue';

export default defineComponent({
  name: 'SingleVoucherForm',

  components: {
    Dropdown,
    InputText,
    InputNumber,
    LocalizedTextInput,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    modelValue: {
      type: Object as PropType<VoucherConfigItem>,
      required: true,
    },

    formValidation: {
      type: Object,
      default: () => ({}),
    },

    allowAutomaticMethod: {
      type: Boolean,
      default: true,
    },

    voucherId: {
      type: String,
      required: true,
    },
  },

  emits: ['update:modelValue'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const currency = computed(() => UserState.activeShop?.currency ?? DEFAULT_CURRENCY);

    const VOUCHER_METHODS = voucherMethodsValues;
    const VOUCHER_TYPES = voucherTypeValues;

    // Local reactive copy of the data
    const localData = computed({
      get: () => props.modelValue,
      set: (value) => emit('update:modelValue', value),
    });

    // Voucher method options
    const voucherMethodOptions = computed(() => {
      const options = [];
      if (props.allowAutomaticMethod) {
        options.push({
          label: t('automatedScenarios.actions.sending_channel.vouchers_config.method.options.automatic'),
          value: VOUCHER_METHODS.AUTOMATIC,
        });
      }
      options.push({
        label: t('automatedScenarios.actions.sending_channel.vouchers_config.method.options.manual'),
        value: VOUCHER_METHODS.MANUAL,
      });
      return options;
    });

    // Voucher type options
    const voucherTypeOptions = computed(() => {
      const baseOptions = [
        {
          label: t('automatedScenarios.actions.sending_channel.vouchers_config.type.options.percentage'),
          value: VOUCHER_TYPES.PERCENTAGE,
        },
        {
          label: t('automatedScenarios.actions.sending_channel.vouchers_config.type.options.amount'),
          value: VOUCHER_TYPES.AMOUNT,
        },
        {
          label: t('automatedScenarios.actions.sending_channel.vouchers_config.type.options.shipping_fees'),
          value: VOUCHER_TYPES.SHIPPING_FEES,
        },
      ];

      if (localData.value.method === VOUCHER_METHODS.AUTOMATIC) {
        baseOptions.push({
          label: t('automatedScenarios.actions.sending_channel.vouchers_config.type.options.duplicated'),
          value: VOUCHER_TYPES.DUPLICATED,
        });
      }

      return baseOptions;
    });

    // Period of validity options
    const periodOfValidityOptions = computed(() => [
      { label: t('automatedScenarios.actions.sending_channel.vouchers_config.period_of_validity.options.one_day'), value: 1 },
      { label: t('automatedScenarios.actions.sending_channel.vouchers_config.period_of_validity.options.two_day'), value: 2 },
      { label: t('automatedScenarios.actions.sending_channel.vouchers_config.period_of_validity.options.three_day'), value: 3 },
      { label: t('automatedScenarios.actions.sending_channel.vouchers_config.period_of_validity.options.four_day'), value: 4 },
      { label: t('automatedScenarios.actions.sending_channel.vouchers_config.period_of_validity.options.five_day'), value: 5 },
      { label: t('automatedScenarios.actions.sending_channel.vouchers_config.period_of_validity.options.six_day'), value: 6 },
      { label: t('automatedScenarios.actions.sending_channel.vouchers_config.period_of_validity.options.one_week'), value: 7 },
      { label: t('automatedScenarios.actions.sending_channel.vouchers_config.period_of_validity.options.two_week'), value: 14 },
      { label: t('automatedScenarios.actions.sending_channel.vouchers_config.period_of_validity.options.three_week'), value: 21 },
      { label: t('automatedScenarios.actions.sending_channel.vouchers_config.period_of_validity.options.four_week'), value: 28 },
      { label: t('automatedScenarios.actions.sending_channel.vouchers_config.period_of_validity.options.five_week'), value: 35 },
      { label: t('automatedScenarios.actions.sending_channel.vouchers_config.period_of_validity.options.six_week'), value: 42 },
      { label: t('automatedScenarios.actions.sending_channel.vouchers_config.period_of_validity.options.seven_week'), value: 49 },
      { label: t('automatedScenarios.actions.sending_channel.vouchers_config.period_of_validity.options.eight_week'), value: 56 },
      { label: t('automatedScenarios.actions.sending_channel.vouchers_config.period_of_validity.options.ten_week'), value: 70 },
      { label: t('automatedScenarios.actions.sending_channel.vouchers_config.period_of_validity.options.twelve_week'), value: 84 },
      { label: t('automatedScenarios.actions.sending_channel.vouchers_config.period_of_validity.options.fourteen_week'), value: 98 },
      { label: t('automatedScenarios.actions.sending_channel.vouchers_config.period_of_validity.options.sixteen_week'), value: 112 },
      { label: t('automatedScenarios.actions.sending_channel.vouchers_config.period_of_validity.options.eighteen_week'), value: 126 },
      { label: t('automatedScenarios.actions.sending_channel.vouchers_config.period_of_validity.options.twenty_week'), value: 140 },
      { label: t('automatedScenarios.actions.sending_channel.vouchers_config.period_of_validity.options.twenty_two_week'), value: 154 },
      { label: t('automatedScenarios.actions.sending_channel.vouchers_config.period_of_validity.options.twenty_four_week'), value: 168 },
      { label: t('automatedScenarios.actions.sending_channel.vouchers_config.period_of_validity.options.twenty_six_week'), value: 182 },
    ]);

    // Reset type to PERCENTAGE if DUPLICATED is selected but method changes to MANUAL
    watch(() => localData.value.method, (newMethod) => {
      if (newMethod === VOUCHER_METHODS.MANUAL
        && localData.value.type === VOUCHER_TYPES.DUPLICATED) {
        localData.value.type = VOUCHER_TYPES.PERCENTAGE;
      }
    });

    return {
      t,
      localData,
      currency,
      voucherMethodOptions,
      voucherTypeOptions,
      periodOfValidityOptions,
      VOUCHER_METHODS,
      VOUCHER_TYPES,
    };
  },
});
</script>

<style scoped lang="scss">
.single-voucher-form {
  .field-group {
    .field-group-content {
      .field-wrapper {
        label {
          text-align: left;
          display: block;
          font-weight: 500;
          color: #495057;

          i {
            margin-left: 0.25rem;
            color: #6c757d;
          }
        }
      }
    }
  }

  :deep(.p-dropdown) {
    .p-dropdown-label {
      text-align: left;
    }
  }

  :deep(.p-inputtext) {
    text-align: left;
  }

  :deep(.p-inputnumber-input) {
    text-align: left;
  }
}
</style>
